import React, { useState, useEffect } from 'react';
import { UpdateOrderAddonCategoryType } from 'models/addon-category.model';
import { ReactComponent as TrashIcon } from 'assets/icons/trash-icon.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check-icon.svg';
import { formatNumber } from 'utils/functions';
import { ENUM_ADDON_SELECTION } from 'models/addon-category.model';
import { OrderAddonType } from 'models/addon.model';

type Props = {
  addonCategory: UpdateOrderAddonCategoryType;
  onReplace: (currentAddon: OrderAddonType, newAddon: OrderAddonType) => void;
  onAdd: (addon: OrderAddonType) => void;
  onDelete: (addon: OrderAddonType) => void;
};

const AddonCategoryCard = ({ addonCategory, onReplace, onAdd, onDelete }: Props) => {
  const [selected, setSelected] = useState<OrderAddonType[]>(() =>
    addonCategory.addons.filter((a) => a.quantity > 0)
  );

  const isSingleSelection = addonCategory.selection_type === ENUM_ADDON_SELECTION.SINGLE_SELECTION;
  const isMultiSelection = addonCategory.selection_type === ENUM_ADDON_SELECTION.MULTI_SELECTION;
  const isMultiSelectionWithQuantity =
    addonCategory.selection_type === ENUM_ADDON_SELECTION.MULTI_SELECTION_WITH_QUANTITY;
  const max_selection = addonCategory.max_selection;

  const addAddon = (addon: OrderAddonType) => {
    if (isSingleSelection) {
      if (selected.find((a) => a.id === addon.id)) deleteAddon(addon);
      else {
        const current = selected.find((a) => a.id !== addon.id);
        if (current) onReplace(current, addon);
        else onAdd(addon);
        setSelected([addon]);
      }
    } else if (isMultiSelection) {
      if (selected.find((a) => a.id === addon.id)) deleteAddon(addon);
      else if (selected.length < max_selection) {
        onAdd(addon);
        setSelected([...selected, addon]);
      }
    } else if (isMultiSelectionWithQuantity) {
      if (selected.find((a) => a.id === addon.id)) onAdd(addon);
      else if (selected.length < max_selection) {
        onAdd(addon);
        if (!selected.find((a) => a.id === addon.id)) setSelected([...selected, addon]);
      }
    }
  };

  useEffect(() => {
    setSelected(addonCategory.addons.filter((a) => a.quantity > 0));
  }, [addonCategory]);

  const deleteAddon = (addon: OrderAddonType) => {
    setSelected(selected.filter((a) => a.id !== addon.id));
    onDelete(addon);
  };
  return (
    <li className='w-full flex flex-col sm:flex-row bg-white  overflow-hidden'>
      <div className='grow pl-2'>
        <article className=' py-3 px-2 border-b border-gray-2 bg-gray-11 '>
          <p className='font-medium'>{addonCategory?.name}</p>
          <p className='text-xs text-[#6F7376]'>{addonCategory.summary}</p>
        </article>

        <ul>
          {addonCategory.addons.map((addon) => (
            <li
              key={addon.id}
              className='group flex items-center gap-2 cursor-pointer select-none'
              onClick={() => addAddon(addon)}>
              <div
                className={`w-[3px] h-9 ${
                  isMultiSelectionWithQuantity && addon.quantity ? 'bg-[#E60A2B]' : ''
                }`}
              />
              <p className='grow flex items-center text-sm py-4 pr-7 border-b border-gray-3 group-last:border-b-0 hover:bg-[#fbfbfb]'>
                {isMultiSelectionWithQuantity && addon.quantity ? (
                  <span className='text-[#E60A2B]'>
                    {addon.quantity ? addon.quantity + 'x' : ''}&nbsp;&nbsp;&nbsp;
                  </span>
                ) : null}
                {addon.name}&nbsp;
                {addon.amount ? (
                  <span className='text-[#6F7376]'>(+{formatNumber(addon.amount)})</span>
                ) : null}
                {addon.quantity ? (
                  <span
                    className='w-5 h-5 flex items-center justify-center ml-auto'
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteAddon(addon);
                    }}>
                    {isSingleSelection ? (
                      <span className='w-4 h-4 flex items-center justify-center rounded-full ml-auto bg-[#E60A2B]'>
                        <CheckIcon />
                      </span>
                    ) : isMultiSelection ? (
                      <span className='w-4 h-4 flex items-center justify-center rounded ml-auto bg-[#E60A2B]'>
                        <CheckIcon />
                      </span>
                    ) : (
                      <TrashIcon />
                    )}
                  </span>
                ) : !isSingleSelection ? (
                  <span className='w-4 h-4 m-[1px] rounded border border-gray-800 ml-auto hover:border-[#E60A2B]' />
                ) : (
                  <span className='w-4 h-4 m-[1px]  border border-gray-800 ml-auto rounded-full' />
                )}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
};

export default AddonCategoryCard;
